import React from 'react';
import { useBreakpoints } from '../../lib/hooks/useBreakpoints';
import { Modal } from './Modal';
import { MobileDrawer, MobileDrawerProps } from './MobileDrawer';

export interface ResponsiveModalProps {
  /** Whether the modal/drawer is open */
  isOpen: boolean;
  /** Callback when modal/drawer should close */
  onClose: () => void;
  /** Modal/Drawer title */
  title: string;
  /** Content */
  children: React.ReactNode;
  /** Size for desktop modal */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'fullscreen';
  /** Allow closing (both modal and drawer) */
  closeable?: boolean;
  /** Mobile drawer height (default: 'auto') */
  mobileHeight?: 'auto' | 'half' | 'full';
  /** Show handle bar on mobile drawer */
  showHandle?: boolean;
  /** Enable swipe to close on mobile */
  swipeToClose?: boolean;
  /** Force mobile or desktop mode (overrides breakpoint detection) */
  forceMode?: 'mobile' | 'desktop';
  /** Custom className for content */
  className?: string;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Remove padding from mobile drawer content (use when content has its own padding) */
  noPadding?: boolean;
  /** Optional content to render on the right side of the header (desktop only) */
  headerRight?: React.ReactNode;
}

/**
 * Responsive modal component that automatically switches between
 * desktop Modal and mobile Drawer based on screen size.
 *
 * On mobile (< 768px): Renders as bottom drawer with swipe-to-close
 * On desktop (≥ 768px): Renders as centered modal
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * return (
 *   <>
 *     <button onClick={() => setIsOpen(true)}>Open</button>
 *     <ResponsiveModal
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *       title="Edit Client"
 *       size="lg"
 *       mobileHeight="full"
 *     >
 *       <ClientForm />
 *     </ResponsiveModal>
 *   </>
 * );
 * ```
 */
export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeable = true,
  mobileHeight = 'auto',
  showHandle = true,
  swipeToClose = true,
  forceMode,
  className = '',
  footer,
  noPadding = false,
  headerRight,
}) => {
  const { isMobile } = useBreakpoints();

  // Determine which component to render
  const useMobileDrawer = forceMode === 'mobile' || (forceMode !== 'desktop' && isMobile);

  if (useMobileDrawer) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        height={mobileHeight}
        showHandle={showHandle}
        swipeToClose={swipeToClose}
        preventBackdropClose={!closeable}
        className={className}
        footer={footer}
        noPadding={noPadding}
      >
        {children}
      </MobileDrawer>
    );
  }

  // Desktop Modal
  const isWideOrFullscreen = size === 'wide' || size === 'fullscreen';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      closeable={closeable}
      headerRight={headerRight}
    >
      <div className={`${isWideOrFullscreen ? 'h-full' : ''} ${className}`}>
        {children}
      </div>
      {footer && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          {footer}
        </div>
      )}
    </Modal>
  );
};

/**
 * Hook to manage responsive modal state
 *
 * @example
 * ```tsx
 * const modal = useResponsiveModal();
 *
 * return (
 *   <>
 *     <button onClick={modal.open}>Open Modal</button>
 *     <ResponsiveModal {...modal} title="My Modal">
 *       <ModalContent />
 *     </ResponsiveModal>
 *   </>
 * );
 * ```
 */
export const useResponsiveModal = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
    onClose: () => setIsOpen(false),
  };
};
